import { Controller } from '@hotwired/stimulus';
import { Helpers } from '../helpers';
import * as bootstrap from 'bootstrap';

export default class InvestFormController extends Controller {
  static targets = [
    'subscriptionAmount',
    'feesAmount',
    'fieldsErrors',
    'subscriptionAmountDivs',
    'feesAmountDivs',
    'totalAmountDivs',
    'nonInformedFields',
    'modalTotalWithFees',
    'submitButton',
    'insufficientBalance',
    'subscribeModal',
    'amountLimits',
  ];

  static values = {
    feesUrl: String,
    projectId: String,
    profileBalance: Number,
    maxInvestRecommendation: Number,
    userMayHaveWarning: Boolean,
  };

  // Compute fees and total through backend
  computeFeesAndTotal() {
    const amount = this.subscriptionAmountTarget.value;
    let controller = this;

    Rails.ajax({
      type: 'GET',
      url: this.feesUrlValue,
      data: 'amount=' + amount + '&project_id=' + this.projectIdValue,
      dataType: 'json',
      success: function (response) {
        const feesAmount = parseFloat(response['fees_amount']);
        const totalWithFeesAmount = parseFloat(response['total_with_fees']);

        // Check if the amount is valid
        controller.checkAmountValidity(amount, totalWithFeesAmount);

        // Display fees and total
        controller.feesAmountTarget.value = feesAmount;
        controller.subscriptionAmountDivsTargets.innerHTML =
          controller.updateAmounts(
            controller.subscriptionAmountDivsTargets,
            parseFloat(amount)
          );
        controller.feesAmountDivsTargets.innerHTML = controller.updateAmounts(
          controller.feesAmountDivsTargets,
          feesAmount
        );
        controller.totalAmountDivsTargets.innerHTML = controller.updateAmounts(
          controller.totalAmountDivsTargets,
          totalWithFeesAmount
        );
        controller.modalTotalWithFeesTarget.innerHTML =
          Helpers.numberToCurrency(totalWithFeesAmount, 2);
      },
      error: function () {
        controller.fieldsErrorsTarget.innerHTML =
          'Une erreur s\'est produite, veuillez réessayer.';
      },
    });
  }

  checkAmountValidity(amount, totalWithFeesAmount) {
    this.insufficientBalanceTarget.classList.add('d-none');
    this.fieldsErrorsTarget.innerHTML = '';
    this.subscriptionAmountTarget.classList.remove('is-invalid');

    const profileBalance = parseFloat(this.profileBalanceValue);

    switch (true) {
      case amount === '':
        this.fieldsErrorsTarget.innerHTML = this.renderError(
          'Un montant de souscription doit être renseigné.'
        );
        this.amountLimitsTarget.classList.add('text-danger');
        this.amountLimitsTarget.classList.remove('text-gray-600');
        return this.triggerError();

      case !this.subscriptionAmountTarget.checkValidity():
        this.fieldsErrorsTarget.innerHTML = this.renderError(
          this.amountValidationMessage()
        );
        this.amountLimitsTarget.classList.add('text-danger');
        this.amountLimitsTarget.classList.remove('text-gray-600');
        return this.triggerError();

      case totalWithFeesAmount > profileBalance:
        this.insufficientBalanceTarget.classList.remove('d-none');
        return this.triggerError();

      default:
        this.fieldsErrorsTarget.innerHTML = '';
        this.subscriptionAmountTarget.classList.remove('is-invalid');
        this.amountLimitsTarget.classList.remove('text-danger');
        this.amountLimitsTarget.classList.add('text-gray-600');
        return true;
    }
  }

  // The native validationMessage omits the two nearest valid values on
  // Safari/WebKit (iOS included); rebuild it from the field's min/step so the
  // complete message shows on every browser. Other errors keep the native text.
  amountValidationMessage() {
    const input = this.subscriptionAmountTarget;
    const { stepMismatch, rangeUnderflow, rangeOverflow } = input.validity;

    if (!stepMismatch || rangeUnderflow || rangeOverflow) {
      return input.validationMessage;
    }

    const min = parseFloat(input.min);
    const max = parseFloat(input.max);
    const step = parseFloat(input.step);
    const value = parseFloat(input.value);

    const lower = min + Math.floor((value - min) / step) * step;
    const upper = lower + step;
    const lowerLabel = Helpers.numberToCurrency(lower, 0);
    const intro = 'Veuillez saisir une valeur valide.';

    if (upper > max) {
      return `${intro} La valeur valide la plus proche est ${lowerLabel}.`;
    }

    const upperLabel = Helpers.numberToCurrency(upper, 0);

    return `${intro} Les deux valeurs valides les plus proches sont ${lowerLabel} et ${upperLabel}.`;
  }

  updateAmounts(container, amount) {
    const finalAmount =
      amount === 0 || isNaN(amount)
        ? '-,-- €'
        : Helpers.numberToCurrency(amount, 2);

    container.forEach((element) => {
      element.innerHTML = finalAmount;
    });
  }

  triggerError() {
    this.subscriptionAmountTarget.classList.add('is-invalid');
    return false;
  }

  validateAndShowSubscriptionModal() {
    const amount = this.subscriptionAmountTarget.value;
    const maxInvestRecommendation = this.maxInvestRecommendationValue;
    const totalWithFeesAmount =
      parseFloat(amount) + parseFloat(this.feesAmountTarget.value);

    if (!this.checkAmountValidity(amount, totalWithFeesAmount)) return;

    // Always reset visibility before showing the modal so that reducing the amount
    // after a first attempt above the recommendation properly hides the warning.
    this.nonInformedFieldsTargets.forEach((element) =>
      element.classList.add('d-none')
    );

    if (
      this.userMayHaveWarningValue === true &&
      amount > maxInvestRecommendation
    ) {
      this.nonInformedFieldsTargets.forEach((element) =>
        element.classList.remove('d-none')
      );
    }

    this.showModal(this.subscribeModalTarget);
  }

  preventAutoSubmit(event) {
    event.preventDefault();
    this.validateAndShowSubscriptionModal();
  }

  showModal(modalTarget) {
    const modal = new bootstrap.Modal(modalTarget);
    modal.show();
  }

  renderError(message) {
    return `
      <span class="d-flex align-items-center gap-1">
        <span class="col-auto mb-1">
          ${this.infoIcon()}
        </span>
        <span>${message}</span>
      </span>
    `;
  }

  infoIcon() {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <path d="M11 15V11M11 7H11.01M21 11C21 16.5228 16.5228 21 11 21C5.47715 21 1 16.5228 1 11C1 5.47715 5.47715 1 11 1C16.5228 1 21 5.47715 21 11Z" stroke="#CD3535" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
  }
}
